import { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  Card,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material'
import { useAnalyticsStore } from '../../stores/analyticsStore'
import { useUserStore } from '../../stores/userStore'
import { getScreenLabel, SCREEN_PAGE_INFO } from '../../utils/screenNames'
import { formatDuration } from '../../utils/formatDuration'

const SCREEN_OPTIONS = SCREEN_PAGE_INFO.map((p) => ({
  label: p.label,
  key: p.keys.split(' / ')[0],
}))

export default function PageUserBreakdownPanel() {
  const { pageUserBreakdown, fetchPageUserBreakdown } = useAnalyticsStore()
  const { users, fetchUsers } = useUserStore()
  const [selectedScreen, setSelectedScreen] = useState<{ label: string; key: string } | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  useEffect(() => {
    if (!selectedScreen) return
    setLoading(true)
    fetchPageUserBreakdown(selectedScreen.key).finally(() => setLoading(false))
  }, [selectedScreen?.key])

  const getUserName = (userId: string) => {
    const u = users.find((u) => u.id === userId)
    return u?.displayName ?? userId
  }

  const records = selectedScreen ? (pageUserBreakdown[selectedScreen.key] ?? null) : null

  return (
    <Box>
      <Card sx={{ p: 3, mb: 3 }}>
        <Autocomplete
          sx={{ maxWidth: 400 }}
          options={SCREEN_OPTIONS}
          value={selectedScreen}
          onChange={(_, v) => setSelectedScreen(v)}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(a, b) => a.key === b.key}
          renderInput={(params) => (
            <TextField {...params} label="選擇頁面" size="small" />
          )}
        />
      </Card>

      {!selectedScreen ? (
        <Alert severity="info">請選擇頁面以檢視使用者明細。</Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !records || records.length === 0 ? (
        <Alert severity="info">
          「{getScreenLabel(selectedScreen.key)}」目前尚無使用者資料。
        </Alert>
      ) : (
        <Card>
          <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
            <Typography variant="subtitle2">
              {getScreenLabel(selectedScreen.key)}　共 {records.length} 位使用者
            </Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>使用者</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>造訪次數</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>累計停留時間</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>最後造訪</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((r) => (
                <TableRow key={r.userId} hover>
                  <TableCell>{getUserName(r.userId)}</TableCell>
                  <TableCell align="right">{r.visits}</TableCell>
                  <TableCell align="right">{formatDuration(r.totalSeconds)}</TableCell>
                  <TableCell align="right">
                    {r.lastVisitAt ? r.lastVisitAt.toLocaleString('zh-TW') : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </Box>
  )
}
